import type { User } from 'firebase/auth';
import { cache } from 'react';

import type { ServiceConfig } from '@/services/httpServices/types';
import { getClaimsFromJWTCookie } from '@/utils/auth-helpers';
import { auth } from '@/utils/firebase-helpers';

import { getUserFeatures, getUserRoleByClaims } from './helpers';
import type { DTOPermissions, UserClaims } from './types';

/**
 * Get user claims from available auth sources
 *
 * Priority order:
 * 1. Firebase Auth (if currentUser exists)
 * 2. JWT cookie decode (fallback for direct JWT login without Firebase)
 *
 * @returns UserClaims or null if no auth source available
 *
 * Wrapped in React `cache()` so repeated calls within a single server request
 * (layout + nested layouts + page) share one Firebase hit instead of re-fetching
 * claims for every server component. Takes no arguments, so the per-request cache
 * key is stable. On the client there is no request scope, so this is a pass-through
 * and the force-refresh path below is preserved.
 */
export const getUserClaims = cache(async (): Promise<UserClaims | null> => {
  const { currentUser } = await auth();

  // Priority 1: Use Firebase Auth if available
  if (currentUser !== null && currentUser !== undefined) {
    // Force refresh only on client-side to get the latest claims from Firebase Auth
    // Server-side (FirebaseServerApp) does not support token refresh operations
    // as it's initialized with a pre-existing token from cookies
    const isServer = typeof window === 'undefined';
    const idToken = await currentUser.getIdTokenResult(!isServer);
    return idToken.claims as UserClaims;
  }

  // Priority 2: Decode JWT cookie directly (for direct JWT login without Firebase Auth)
  const jwtClaims = getClaimsFromJWTCookie();
  if (jwtClaims !== null) {
    return jwtClaims;
  }

  return null;
});

export async function getUserPermissionsService(config: ServiceConfig): Promise<DTOPermissions> {
  const claims = await getUserClaims();
  const activeSpace = config.httpRequestConfig.space;

  const nullRolePayload = {
    role: null,
    features: [],
  };

  if (claims === null) {
    return nullRolePayload;
  }

  const role = getUserRoleByClaims(claims, activeSpace);

  if (role === null) {
    return nullRolePayload;
  }

  const userFeatures = getUserFeatures(claims, activeSpace);

  return { role, features: userFeatures };
}

export async function getUserService(): Promise<User | null> {
  const { currentUser } = await auth();

  // Return the raw Firebase user object if available, otherwise return null
  return currentUser ?? null;
}
